import type { Customer } from './api';

/** Two-line city block — `psc mesto` on one line, `ulice cislo` on the other. */
export function cityLine(c: Pick<Customer, 'psc' | 'mesto'>): string {
  return [c.psc, c.mesto].filter(Boolean).join(' ').trim();
}

export function streetLine(c: Pick<Customer, 'ulice' | 'cislo_popisne'>): string {
  return [c.ulice, c.cislo_popisne].filter(Boolean).join(' ').trim();
}

/** "Stavební firma Holík s.r.o." → `SH`. */
export function customerInitials(jmeno: string): string {
  const cleaned = jmeno.replace(/[„""].+?[""]/g, '').trim();
  const parts = cleaned.split(/\s+/).filter((p) => /^[A-Za-zÀ-ž]/.test(p));
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ==================== Bank details ====================
//
// Mirrors `api/src/utils/bank-account.util.ts` — the API is the boundary, this
// only gives the form inline feedback and the detail page a display format.

const CISLO_WEIGHTS = [6, 3, 7, 9, 10, 5, 8, 4, 2, 1];
const PREDCISLI_WEIGHTS = [10, 5, 8, 4, 2, 1];

function mod11(digits: string, weights: number[]): boolean {
  const padded = digits.padStart(weights.length, '0');
  let sum = 0;
  for (let i = 0; i < weights.length; i++) sum += Number(padded[i]) * weights[i];
  return sum % 11 === 0;
}

export function stripSpaces(raw: string): string {
  return raw.replace(/\s+/g, '');
}

export function isValidCzPredcisli(predcisli: string): boolean {
  if (predcisli === '') return true;
  return /^\d{1,6}$/.test(predcisli) && mod11(predcisli, PREDCISLI_WEIGHTS);
}

export function isValidCzCislo(cislo: string): boolean {
  if (!/^\d{2,10}$/.test(cislo)) return false;
  if ((cislo.match(/[1-9]/g) ?? []).length < 2) return false;
  return mod11(cislo, CISLO_WEIGHTS);
}

export function isValidKodBanky(kod: string): boolean {
  return /^\d{4}$/.test(kod);
}

export function normalizeIban(raw: string): string {
  return stripSpaces(raw).toUpperCase();
}

export function isValidIban(iban: string): boolean {
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const v = ch >= 'A' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of v) remainder = (remainder * 10 + Number(d)) % 97;
  }
  return remainder === 1;
}

/** `19-2000145399/0800` (prefix omitted when empty); empty string when no account. */
export function formatCzAccount(
  c: Pick<Customer, 'ucet_predcisli' | 'ucet_cislo' | 'ucet_kod_banky'>,
): string {
  if (!c.ucet_cislo || !c.ucet_kod_banky) return '';
  const prefix = c.ucet_predcisli ? `${c.ucet_predcisli}-` : '';
  return `${prefix}${c.ucet_cislo}/${c.ucet_kod_banky}`;
}

/** `CZ65 0800 0000 1920 0014 5399` — groups of four for reading. */
export function formatIban(iban: string | null): string {
  if (!iban) return '';
  return normalizeIban(iban).replace(/(.{4})(?=.)/g, '$1 ');
}
